/**
 * Adoption listings.
 *
 * Same blueprint as Customers and Vendors — segments own the status filter, the
 * metric tiles are links that apply their own filter, and every filter and sort
 * goes to the server.
 *
 * What makes this module different is who posts here. A vendor is a business
 * that went through KYC; an adoption listing is an ordinary person giving away
 * an animal, with no vetting at all. So the triage signals are quality and
 * safety rather than lifecycle: no photo, an unverified phone number, nobody
 * interested. Those are the tiles, because they are the reasons a moderator
 * opens this page.
 */
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';
import {
  Copy,
  Eye,
  EyeOff,
  Heart,
  ImageOff,
  MapPin,
  MoreHorizontal,
  PhoneOff,
  Trash2,
  Users,
} from 'lucide-react';
import {
  bool_param,
  enum_param,
  page_param,
  page_size_param,
  sort_param,
  string_param,
  use_url_state,
} from '@/hooks/use-url-state';
import { DashboardLayout, PageTabs } from '@/components/dashboard-layout';
import { DataTable } from '@/components/data-table/data-table';
import {
  BoolFilter,
  FilterBar,
  SearchInput,
  SelectFilter,
  type ChipInput,
} from '@/components/filters/filter-bar';
import { DateRangeFilter, describe_range } from '@/components/filters/date-range-filter';
import { SavedViews } from '@/components/filters/saved-views';
import { MetricStrip, type Metric } from '@/components/metric-strip';
import { PersonAvatar } from '@/components/person-avatar';
import { AuthImage } from '@/components/auth-image';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { bulk_btn, bulk_btn_danger } from '@/components/data-table/bulk-bar';
import { ReasonModal } from '@/features/vendors/components/reason-modal';
import { notify } from '@/components/ui/toaster';
import { ago, title_case } from '@/lib/format';
import { export_adoption_listings } from '@/features/adoption/api';
import {
  use_adoption_counts,
  use_adoption_list,
  use_bulk_moderate_listings,
  use_moderate_listing,
} from '@/features/adoption/hooks';
import {
  ADOPTION_SORT_FIELDS,
  ADOPTION_SPECIES_OPTIONS,
  ADOPTION_STATUS_LABELS,
  ADOPTION_STATUS_OPTIONS,
  type AdoptionListFilters,
  type AdoptionListItem,
  type AdoptionListingStatus,
  type AdoptionModerationAction,
} from '@/features/adoption/types';
import { cn } from '@/lib/utils';

/**
 * Lifecycle segments.
 *
 * "Needs a look" is the standing job here: live listings missing a photo or an
 * unverified number are the two shapes of low-quality post, and an operator
 * should not have to compose that filter by hand every morning. It is a status
 * plus a quality filter rather than a status alone, which is why it is a
 * segment and not just a tile.
 */
const SEGMENTS = ['live', 'review', 'drafts', 'moderated', 'settled', 'all'] as const;
type Segment = (typeof SEGMENTS)[number];

const SEGMENT_FILTERS: Record<Segment, Partial<AdoptionListFilters>> = {
  live: { statuses: 'PUBLISHED' },
  review: { statuses: 'PUBLISHED', has_photos: 'false' },
  drafts: { statuses: 'DRAFT' },
  moderated: { statuses: 'HIDDEN,REMOVED' },
  settled: { statuses: 'ADOPTED,CLOSED' },
  all: {},
};

const SEGMENT_COPY: Record<Segment, { label: string; title: string; blurb: string }> = {
  live: {
    label: 'Live',
    title: 'Live listings',
    blurb: 'Everything a person searching for a pet can see right now',
  },
  review: {
    label: 'Needs a look',
    title: 'Listings needing a look',
    blurb: 'Live listings with no photo — nothing for an adopter to go on',
  },
  drafts: {
    label: 'Drafts',
    title: 'Draft listings',
    blurb: 'Started but never published, usually because the phone was never verified',
  },
  moderated: {
    label: 'Hidden & removed',
    title: 'Moderated listings',
    blurb: 'Pulled from the feed by an admin, with the reason on each record',
  },
  settled: {
    label: 'Closed',
    title: 'Closed listings',
    blurb: 'Adopted, or withdrawn by the person who posted them',
  },
  all: { label: 'All', title: 'Adoption listings', blurb: 'Every listing ever posted' },
};

/** The colour a status carries everywhere in this module. */
const STATUS_TONE: Record<
  AdoptionListingStatus,
  'success' | 'warning' | 'neutral' | 'destructive' | 'info'
> = {
  PUBLISHED: 'success',
  DRAFT: 'warning',
  ADOPTED: 'info',
  CLOSED: 'neutral',
  HIDDEN: 'warning',
  REMOVED: 'destructive',
};

export function AdoptionListPage() {
  const [segment_raw, set_segment] = use_url_state('segment', enum_param(SEGMENTS));
  const segment: Segment = segment_raw || 'live';
  const copy = SEGMENT_COPY[segment];

  const [q, set_q] = use_url_state('q', string_param);
  const [species, set_species] = use_url_state('species', enum_param(ADOPTION_SPECIES_OPTIONS));
  const [city, set_city] = use_url_state('city', string_param);
  const [status, set_status] = use_url_state('status', enum_param(ADOPTION_STATUS_OPTIONS));
  const [phone_verified, set_phone_verified] = use_url_state('phone_verified', bool_param);
  const [has_photos, set_has_photos] = use_url_state('has_photos', bool_param);
  const [has_requests, set_has_requests] = use_url_state('has_requests', bool_param);
  const [vaccinated, set_vaccinated] = use_url_state('vaccinated', bool_param);
  const [sterilized, set_sterilized] = use_url_state('sterilized', bool_param);
  const [from, set_from] = use_url_state('from', string_param);
  const [to, set_to] = use_url_state('to', string_param);
  const [page, set_page] = use_url_state('page', page_param);
  const [page_size, set_page_size] = use_url_state('page_size', page_size_param);
  const [sort, set_sort] = use_url_state(
    'sort',
    sort_param(ADOPTION_SORT_FIELDS, { by: 'created_at', dir: 'desc' }),
  );

  const reset_page = () => set_page(1);

  const segment_filters = SEGMENT_FILTERS[segment];

  const filters: AdoptionListFilters = useMemo(
    () => ({
      ...segment_filters,
      page,
      page_size,
      q: q || undefined,
      // A status picked by hand wins over the segment's own set: an operator who
      // narrows "Live" to "Adopted" means it, and silently ignoring them is how
      // a filter ends up looking alive while doing nothing.
      ...(status ? { statuses: undefined, status } : {}),
      species: species || undefined,
      city: city || undefined,
      phone_verified: phone_verified || undefined,
      // The segment owns this one; a hand-set value only applies elsewhere.
      has_photos: segment === 'review' ? 'false' : has_photos || undefined,
      has_requests: has_requests || undefined,
      vaccinated: vaccinated || undefined,
      sterilized: sterilized || undefined,
      from: from || undefined,
      to: to || undefined,
    }),
    [
      segment,
      segment_filters,
      page,
      page_size,
      q,
      status,
      species,
      city,
      phone_verified,
      has_photos,
      has_requests,
      vaccinated,
      sterilized,
      from,
      to,
    ],
  );

  const { data, isLoading, isFetching, error, refetch } = use_adoption_list(filters, sort);
  const { data: counts } = use_adoption_counts();

  /* ------------------------------- moderation ------------------------------ */

  const [modal, set_modal] = useState<{
    ids: string[];
    action: AdoptionModerationAction;
    label: string;
  } | null>(null);
  const [modal_error, set_modal_error] = useState<string | null>(null);

  // One listing goes through the single endpoint so the response is the updated
  // record; a selection goes through the bulk one, which reports what it skipped.
  const single = use_moderate_listing(modal?.ids[0] ?? '');
  const bulk = use_bulk_moderate_listings();
  const pending = single.isPending || bulk.isPending;

  function apply(reason: string, clear_selection?: () => void) {
    if (!modal) return;
    set_modal_error(null);
    const done = () => {
      set_modal(null);
      clear_selection?.();
    };

    if (modal.ids.length === 1) {
      single.mutate(
        { action: modal.action, reason },
        {
          onSuccess: (updated) => {
            done();
            notify.success(`Listing ${past_tense(modal.action)}`, {
              description: `Now ${ADOPTION_STATUS_LABELS[updated.status].toLowerCase()}. The person who posted it has been told why.`,
            });
          },
          onError: (err) => {
            set_modal_error(err instanceof Error ? err.message : 'Could not apply that');
            notify.error(err, 'Could not moderate the listing');
          },
        },
      );
      return;
    }

    bulk.mutate(
      { listing_ids: modal.ids, action: modal.action, reason },
      {
        onSuccess: (res) => {
          done();
          if (res.skipped.length > 0) {
            // Never "changed 30" when four were already removed.
            notify.warning(
              `${res.changed} ${past_tense(modal.action)}, ${res.skipped.length} skipped`,
              {
                description: res.skipped[0]?.reason,
              },
            );
          } else {
            notify.success(`${res.changed} listings ${past_tense(modal.action)}`, {
              description: 'Everyone who posted them has been told why.',
            });
          }
        },
        onError: (err) => {
          set_modal_error(err instanceof Error ? err.message : 'Could not apply that');
          notify.error(err, 'Could not moderate the listings');
        },
      },
    );
  }

  /* --------------------------------- chips --------------------------------- */

  const bool_chip = (
    key: string,
    label: string,
    value: string,
    yes: string,
    no: string,
    clear: () => void,
  ) =>
    value
      ? {
          key,
          label,
          value: value === 'true' ? yes : no,
          advanced: true,
          on_clear: () => {
            clear();
            reset_page();
          },
        }
      : null;

  const chips: ChipInput[] = [
    q && { key: 'q', label: 'Search', value: q, on_clear: () => set_q('') },
    status && {
      key: 'status',
      label: 'Status',
      value: ADOPTION_STATUS_LABELS[status],
      advanced: true,
      on_clear: () => set_status(''),
    },
    species && {
      key: 'species',
      label: 'Species',
      value: title_case(species),
      on_clear: () => set_species(''),
    },
    city && { key: 'city', label: 'City', value: city, on_clear: () => set_city('') },
    bool_chip('phone_verified', 'Contact number', phone_verified, 'Verified', 'Unverified', () =>
      set_phone_verified(''),
    ),
    // The "Needs a look" segment owns this filter, so offering an X that cannot
    // do anything would be a lie.
    segment === 'review'
      ? null
      : bool_chip('has_photos', 'Photos', has_photos, 'Has photos', 'None', () =>
          set_has_photos(''),
        ),
    bool_chip('has_requests', 'Interest', has_requests, 'Has adopters', 'Nobody yet', () =>
      set_has_requests(''),
    ),
    bool_chip('vaccinated', 'Vaccinated', vaccinated, 'Yes', 'No', () => set_vaccinated('')),
    bool_chip('sterilized', 'Sterilised', sterilized, 'Yes', 'No', () => set_sterilized('')),
    (from || to) && {
      key: 'date',
      label: 'Posted',
      value: describe_range(from, to),
      on_clear: () => {
        set_from('');
        set_to('');
      },
    },
  ];

  function clear_all() {
    set_q('');
    set_status('');
    set_species('');
    set_city('');
    set_phone_verified('');
    set_has_photos('');
    set_has_requests('');
    set_vaccinated('');
    set_sterilized('');
    set_from('');
    set_to('');
    reset_page();
  }

  /* -------------------------------- metrics -------------------------------- */

  const metrics: Metric[] = useMemo(
    () => [
      {
        key: 'live',
        label: 'Live',
        value: counts?.published?.toLocaleString('en-IN'),
        to: '/adoption?segment=live',
        icon: Heart,
        loading: !counts,
      },
      {
        key: 'no_photo',
        label: 'No photo',
        value: counts?.published_without_photo?.toLocaleString('en-IN'),
        hint: 'Live, with nothing to look at',
        // The commonest low-quality signal, and the reason "Needs a look" exists.
        tone: counts && counts.published_without_photo > 0 ? 'warning' : 'default',
        to: '/adoption?segment=review',
        icon: ImageOff,
        loading: !counts,
      },
      {
        key: 'phone_unverified',
        label: 'Contact unverified',
        value: counts?.published_unverified_phone?.toLocaleString('en-IN'),
        hint: 'Live, with an unproven number',
        tone: counts && counts.published_unverified_phone > 0 ? 'warning' : 'default',
        to: '/adoption?segment=live&phone_verified=false',
        icon: PhoneOff,
        loading: !counts,
      },
      {
        key: 'paid_adopters',
        label: 'Paid adopters',
        // People who paid the contact fee and are now waiting on a stranger to
        // pick up the phone. The one number here with money behind it.
        value: counts?.confirmed_requests?.toLocaleString('en-IN'),
        hint: 'Fee captured, contact unlocked',
        to: '/adoption?segment=all&has_requests=true',
        icon: Users,
        loading: !counts,
      },
      {
        key: 'moderated',
        label: 'Hidden & removed',
        value: counts ? (counts.hidden + counts.removed).toLocaleString('en-IN') : undefined,
        to: '/adoption?segment=moderated',
        icon: EyeOff,
        loading: !counts,
      },
    ],
    [counts],
  );

  /* -------------------------------- columns -------------------------------- */

  const columns = useMemo<ColumnDef<AdoptionListItem>[]>(
    () => [
      {
        id: 'pet',
        header: 'Pet',
        meta: { sort_key: 'species' },
        cell: ({ row }) => {
          const l = row.original;
          return (
            <span className="flex min-w-0 items-center gap-2.5">
              {/*
                The photo is the listing. A moderator judging whether a post is
                real looks at the picture first, and this column used to be text
                only — so every judgement meant opening the record.
              */}
              {l.primary_photo_thumb_path ? (
                <AuthImage
                  path={l.primary_photo_thumb_path}
                  alt=""
                  className="h-9 w-9 shrink-0 rounded-control object-cover"
                />
              ) : (
                <span
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-control bg-muted text-muted-foreground"
                  title="No photo on this listing"
                >
                  <ImageOff aria-hidden="true" className="h-4 w-4" />
                </span>
              )}
              <span className="flex min-w-0 flex-col">
                <span className="truncate font-semibold">
                  {title_case(l.species)}
                  {l.breed ? ` · ${l.breed}` : ''}
                </span>
                <span className="truncate text-micro text-muted-foreground">
                  {[l.age_label, l.gender !== 'UNKNOWN' ? title_case(l.gender) : null]
                    .filter(Boolean)
                    .join(' · ') || 'Age and sex not given'}
                </span>
              </span>
            </span>
          );
        },
      },
      {
        id: 'poster',
        header: 'Posted by',
        enableSorting: false,
        cell: ({ row }) => {
          const p = row.original.poster;
          return (
            <span className="flex min-w-0 items-center gap-2">
              <PersonAvatar path={p.avatar_path ?? null} name={p.name} seed={p.id} size="sm" />
              <span className="flex min-w-0 flex-col">
                <Link
                  to={`/users/${p.id}`}
                  onClick={(e) => e.stopPropagation()}
                  className="truncate font-medium underline-offset-2 hover:text-primary hover:underline"
                >
                  {p.name ?? 'No name on file'}
                </Link>
                <span className="truncate text-micro text-muted-foreground">
                  {p.phone ?? '—'}
                  {/*
                    Not the account's number — the one printed on the listing for
                    adopters to ring. An unverified one is the single strongest
                    signal that a post is not what it says it is.
                  */}
                  {!row.original.phone_verified && (
                    <span className="ml-1.5 text-warning">· contact unverified</span>
                  )}
                </span>
              </span>
            </span>
          );
        },
      },
      {
        accessorKey: 'city',
        header: 'City',
        meta: { sort_key: 'city' },
        cell: ({ row }) => (
          <span className="flex items-center gap-1.5 text-muted-foreground">
            <MapPin aria-hidden="true" className="h-3.5 w-3.5" />
            <span className="truncate">
              {row.original.city}
              {row.original.state ? `, ${row.original.state}` : ''}
            </span>
          </span>
        ),
      },
      {
        id: 'care',
        header: 'Care',
        enableSorting: false,
        cell: ({ row }) => {
          const l = row.original;
          // Tri-state on purpose: "not told us" is not the same as "no", and an
          // adopter is entitled to know which one they are looking at.
          const chips_for = [
            l.vaccinated === true ? { label: 'Vaccinated', tone: 'success' as const } : null,
            l.vaccinated === false ? { label: 'Not vaccinated', tone: 'warning' as const } : null,
            l.sterilized === true ? { label: 'Sterilised', tone: 'success' as const } : null,
          ].filter(Boolean) as { label: string; tone: 'success' | 'warning' }[];
          if (chips_for.length === 0) {
            return <span className="text-muted-foreground/60">Not stated</span>;
          }
          return (
            <span className="flex flex-wrap gap-1">
              {chips_for.map((c) => (
                <Badge key={c.label} tone={c.tone} size="sm">
                  {c.label}
                </Badge>
              ))}
            </span>
          );
        },
      },
      {
        id: 'request_count',
        accessorKey: 'request_count',
        header: 'Interest',
        meta: { align: 'right', sort_key: 'request_count' },
        cell: ({ row }) => (
          <span className="tabular">
            {row.original.request_count > 0 ? (
              <span className="font-semibold">{row.original.request_count}</span>
            ) : (
              <span className="text-muted-foreground/60">0</span>
            )}
          </span>
        ),
      },
      {
        accessorKey: 'status',
        header: 'Status',
        meta: { sort_key: 'status' },
        cell: ({ row }) => (
          <Badge
            tone={STATUS_TONE[row.original.status]}
            size="sm"
            dot
            data-testid={`status-${row.original.status}`}
          >
            {ADOPTION_STATUS_LABELS[row.original.status]}
          </Badge>
        ),
      },
      {
        accessorKey: 'created_at',
        header: 'Posted',
        meta: { align: 'right', sort_key: 'created_at' },
        cell: ({ row }) => (
          <span className="whitespace-nowrap text-muted-foreground" title={row.original.created_at}>
            {ago(row.original.created_at)}
          </span>
        ),
      },
      {
        id: 'actions',
        header: '',
        enableSorting: false,
        meta: { align: 'right' },
        cell: ({ row }) => {
          const l = row.original;
          const can_hide = l.status !== 'HIDDEN' && l.status !== 'REMOVED';
          return (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  onClick={(e) => e.stopPropagation()}
                  className="inline-flex h-7 w-7 items-center justify-center rounded-control text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                  aria-label="Listing actions"
                  data-testid={`row-actions-${l.id}`}
                >
                  <MoreHorizontal className="h-4 w-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <RowAction
                  on_select={() => {
                    void navigator.clipboard.writeText(l.id);
                    notify.success('Listing ID copied');
                  }}
                  testid={`copy-${l.id}`}
                >
                  <Copy className="h-4 w-4" /> Copy listing ID
                </RowAction>
                <DropdownMenuSeparator />
                {can_hide ? (
                  <RowAction
                    on_select={() =>
                      set_modal({ ids: [l.id], action: 'hide', label: 'Hide listing' })
                    }
                    testid={`hide-${l.id}`}
                  >
                    <EyeOff className="h-4 w-4" /> Hide from the feed
                  </RowAction>
                ) : l.status === 'HIDDEN' ? (
                  <RowAction
                    on_select={() =>
                      set_modal({ ids: [l.id], action: 'unhide', label: 'Restore listing' })
                    }
                    testid={`unhide-${l.id}`}
                  >
                    <Eye className="h-4 w-4" /> Restore to the feed
                  </RowAction>
                ) : null}
                {l.status !== 'REMOVED' && (
                  <RowAction
                    on_select={() =>
                      set_modal({ ids: [l.id], action: 'remove', label: 'Remove listing' })
                    }
                    testid={`remove-${l.id}`}
                    destructive
                  >
                    <Trash2 className="h-4 w-4" /> Remove permanently
                  </RowAction>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          );
        },
      },
    ],
    [],
  );

  return (
    <DashboardLayout
      title={copy.title}
      subtitle={copy.blurb}
      actions={<SavedViews module={`adoption:${segment}`} is_filtered={chips.some(Boolean)} />}
      tabs={
        <PageTabs
          current={segment}
          on_change={(v) => {
            set_segment(v as Segment);
            reset_page();
          }}
          tabs={SEGMENTS.map((v) => ({
            value: v,
            label: SEGMENT_COPY[v].label,
            // The one queue worth carrying a number on the tab itself.
            count: v === 'review' ? counts?.published_without_photo : undefined,
          }))}
        />
      }
      stats={<MetricStrip metrics={metrics} />}
    >
      <DataTable<AdoptionListItem>
        columns={columns}
        data={data?.items ?? []}
        total={data?.total ?? 0}
        get_row_id={(l) => l.id}
        page={page}
        page_size={page_size}
        on_page_change={set_page}
        on_page_size_change={(n) => {
          set_page_size(n);
          reset_page();
        }}
        sort={sort}
        on_sort_change={(next) => {
          set_sort(next);
          reset_page();
        }}
        is_loading={isLoading}
        is_fetching={isFetching}
        error={error}
        on_retry={() => void refetch()}
        row_href={(l) => `/adoption/${l.id}`}
        storage_key={`adoption:${segment}`}
        export_name="adoption-listings"
        on_export_all={() => export_adoption_listings(filters, sort)}
        caption="Adoption listings"
        test_id="adoption-list"
        selectable
        bulk_actions={({ ids, clear }) => (
          <>
            <button
              type="button"
              className={bulk_btn}
              onClick={() =>
                set_modal({ ids, action: 'hide', label: `Hide ${ids.length} listings` })
              }
              data-testid="bulk-hide"
            >
              <EyeOff /> Hide
            </button>
            <button
              type="button"
              className={bulk_btn_danger}
              onClick={() =>
                set_modal({ ids, action: 'remove', label: `Remove ${ids.length} listings` })
              }
              data-testid="bulk-remove"
            >
              <Trash2 /> Remove
            </button>
            {/* `bulk-clear` is already taken by the bar's own dismiss control. */}
            <button
              type="button"
              className={bulk_btn}
              onClick={clear}
              data-testid="bulk-clear-selection"
            >
              Clear
            </button>
          </>
        )}
        empty={{
          icon: Heart,
          title: chips.some(Boolean)
            ? 'No listings match these filters'
            : `No ${copy.label.toLowerCase()} listings`,
          description: chips.some(Boolean)
            ? 'Try widening the date range or clearing a filter.'
            : segment === 'review'
              ? 'Every live listing has at least one photo — nothing to chase.'
              : 'Listings appear here as people post pets for adoption in the app.',
        }}
        filters={
          <FilterBar
            chips={chips}
            on_clear_all={clear_all}
            advanced={
              <>
                <SelectFilter
                  label="Status"
                  value={status}
                  on_change={(v) => {
                    set_status(v as AdoptionListingStatus);
                    reset_page();
                  }}
                  options={ADOPTION_STATUS_OPTIONS.map((s) => ({
                    value: s,
                    label: ADOPTION_STATUS_LABELS[s],
                  }))}
                  all_label="Any status"
                  test_id="filter-status"
                />
                <BoolFilter
                  label="Contact number"
                  value={phone_verified}
                  on_change={(v) => {
                    set_phone_verified(v);
                    reset_page();
                  }}
                  true_label="Verified"
                  false_label="Unverified"
                  test_id="filter-phone-verified"
                />
                <BoolFilter
                  label="Photos"
                  value={segment === 'review' ? 'false' : has_photos}
                  on_change={(v) => {
                    set_has_photos(v);
                    reset_page();
                  }}
                  true_label="Has photos"
                  false_label="None"
                  test_id="filter-has-photos"
                />
                <BoolFilter
                  label="Interest"
                  value={has_requests}
                  on_change={(v) => {
                    set_has_requests(v);
                    reset_page();
                  }}
                  true_label="Has adopters"
                  false_label="Nobody yet"
                  test_id="filter-has-requests"
                />
                <BoolFilter
                  label="Vaccinated"
                  value={vaccinated}
                  on_change={(v) => {
                    set_vaccinated(v);
                    reset_page();
                  }}
                  true_label="Yes"
                  false_label="No"
                  test_id="filter-vaccinated"
                />
                <BoolFilter
                  label="Sterilised"
                  value={sterilized}
                  on_change={(v) => {
                    set_sterilized(v);
                    reset_page();
                  }}
                  true_label="Yes"
                  false_label="No"
                  test_id="filter-sterilized"
                />
              </>
            }
          >
            <SearchInput
              value={q}
              on_change={(next) => {
                set_q(next);
                reset_page();
              }}
              label="Search"
              placeholder="Breed, city, contact name or number…"
              test_id="filter-q"
            />
            <SelectFilter
              label="Species"
              value={species}
              on_change={(v) => {
                set_species(v);
                reset_page();
              }}
              options={ADOPTION_SPECIES_OPTIONS.map((s) => ({ value: s, label: title_case(s) }))}
              all_label="Any species"
              test_id="filter-species"
            />
            <SearchInput
              value={city}
              on_change={(next) => {
                set_city(next);
                reset_page();
              }}
              label="City"
              placeholder="Bengaluru…"
              test_id="filter-city"
              className="!flex-none !basis-auto"
            />
            <DateRangeFilter
              from={from}
              to={to}
              on_change={(next) => {
                set_from(next.from);
                set_to(next.to);
                reset_page();
              }}
              label="Posted"
            />
          </FilterBar>
        }
      />

      <ReasonModal
        open={modal !== null}
        title={modal?.label ?? ''}
        description={moderation_description(modal?.action, modal?.ids.length ?? 0)}
        confirm_label={modal ? title_case(modal.action) : ''}
        variant={modal?.action === 'remove' ? 'destructive' : 'default'}
        loading={pending}
        error={modal_error}
        on_confirm={(reason) => apply(reason)}
        on_cancel={() => {
          set_modal(null);
          set_modal_error(null);
        }}
      />
    </DashboardLayout>
  );
}

/**
 * A dropdown item that survives Radix's pointer-based selection.
 *
 * Radix selects on pointerdown/pointerup rather than click, so a plain onClick
 * fires after the menu has begun closing and the row's own navigation wins.
 */
function RowAction({
  children,
  on_select,
  testid,
  destructive,
}: {
  children: React.ReactNode;
  on_select: () => void;
  testid: string;
  destructive?: boolean;
}) {
  return (
    <DropdownMenuItem
      onPointerDown={(e) => e.stopPropagation()}
      onPointerUp={(e) => e.stopPropagation()}
      onSelect={on_select}
      data-testid={testid}
      className={cn(destructive && 'text-destructive focus:text-destructive')}
    >
      {children}
    </DropdownMenuItem>
  );
}

function past_tense(action: AdoptionModerationAction): string {
  return action === 'hide' ? 'hidden' : action === 'unhide' ? 'restored' : 'removed';
}

/** What the operator is about to do, said plainly, before they type a reason. */
function moderation_description(
  action: AdoptionModerationAction | undefined,
  count: number,
): string {
  const subject = count > 1 ? `these ${count} listings` : 'this listing';
  switch (action) {
    case 'hide':
      return `${title_case(subject)} will stop showing in the adoption feed. The reason you give is sent to the ${count > 1 ? 'people' : 'person'} who posted ${count > 1 ? 'them' : 'it'}, and hiding can be undone.`;
    case 'unhide':
      return `${title_case(subject)} will show in the feed again. The reason you give is sent to the ${count > 1 ? 'posters' : 'poster'}.`;
    case 'remove':
      return `${title_case(subject)} will be removed for good — removal cannot be undone from here. The reason you give is sent to the ${count > 1 ? 'people' : 'person'} who posted ${count > 1 ? 'them' : 'it'}.`;
    default:
      return '';
  }
}

export default AdoptionListPage;
